import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { extractOAuthChallenge, performOAuthHandoff } from "./oauthHandoff";

export type CurrentUser = {
  canAccessAdminArena: boolean;
  isComplianceUser: boolean;
  isKycUser: boolean;
  isTreasuryUser: boolean;
  isCanopyUser: boolean;
};

export class NoPermissionError extends Error {
  constructor() {
    super("User has no permissions");
  }
}

export function useHandleSuccessfulLogin() {
  const history = useHistory();
  const location = useLocation();

  /**
   * Redirects to the appropriate page based on the user's permissions. Redirects
   * using a history push if the path is within the same SPA, or a full page redirect
   * otherwise.
   *
   * If the next path is an Ory OAuth login handoff, we bind the authenticated
   * session to the challenge and redirect to the Hydra callback instead.
   *
   * If we cannot find where to redirect the user, we throw a NoPermissionError.
   */
  const redirectOnSuccessfulLogin = useCallback(
    async (user: CurrentUser) => {
      // if there's a next path, and it's a relative path, use that
      const rawNextPath = new URLSearchParams(location.search).get("path");

      // OAuth handoff takes precedence: /login/oauth-handoff is a monolith
      // endpoint, not an in-app route, so it must be driven by a CSRF-protected
      // POST rather than an SPA navigation.
      if (rawNextPath) {
        const challenge = extractOAuthChallenge(rawNextPath);
        if (challenge) {
          await performOAuthHandoff(challenge);
          return;
        }
      }

      let nextPath: string | null = null;

      if (rawNextPath) {
        try {
          // Normalize the path relative to current origin and only use the internal path/query
          const url = new URL(rawNextPath, window.location.origin);
          const candidate = url.pathname + url.search;
          if (
            candidate.startsWith("/cctools") ||
            candidate.startsWith("/canopy") ||
            candidate.startsWith("/kyc")
          ) {
            nextPath = candidate;
          }
        } catch {
          nextPath = null;
        }
      }

      if (nextPath?.startsWith("/cctools")) {
        history.push(nextPath);
        return;
      }

      if (nextPath?.startsWith("/canopy")) {
        const newPath = nextPath.replace("/canopy", "") || "/";
        // Note: a "path=/canopy/login" target would strip to "/login" and
        // self-redirect here. We initially guarded that, but no real flow sets
        // path=/canopy/login (the OAuth handoff is handled above, and return-to
        // deep links never point at the login page), so the guard was closing a
        // gap that can't actually occur — left out of scope for this change.
        history.push(newPath);
        return;
      }

      if (nextPath?.startsWith("/kyc")) {
        window.location.assign(nextPath);
        return;
      }

      if (user.isComplianceUser) {
        history.push("/compliance");
        return;
      }

      if (user.isTreasuryUser) {
        history.push("/treasury");
        return;
      }

      if (user.isCanopyUser) {
        history.push("/");
        return;
      }

      if (user.isKycUser) {
        window.location.assign("/kyc");
        return;
      }

      if (user.canAccessAdminArena) {
        history.push("/cctools");
        return;
      }

      throw new NoPermissionError();
    },
    [history, location.search],
  );

  return { redirectOnSuccessfulLogin };
}
